'use client'

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
} from 'lucide-react'
import {
  ExpandDemoVideoButton,
  LandingDemoVideoLightbox,
} from '@/app/(marketing)/_components/landing-demo-video-lightbox'
import { Button } from '@/components/ui/button'
import {
  fetchLandingCarouselSlides,
  getLandingCarouselFallbackSlides,
  type LandingCarouselViewSlide,
} from '@/lib/cms/landing-carousel'
import { cn } from '@/lib/utils'

function SlideIcon({
  slide,
  className,
}: {
  slide: LandingCarouselViewSlide
  className?: string
}) {
  // baby: uploaded icon image wins over Lucide name
  if (slide.iconUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={slide.iconUrl} alt="" className={cn('object-contain', className)} />
    )
  }
  const IconComponent = slide.icon
  return <IconComponent className={className} style={{ color: slide.color }} />
}

export const NinxlFeatureCarousel = memo(function NinxlFeatureCarousel() {
  const { data: demos } = useQuery({
    queryKey: ['landing-carousel', 'ninxl'],
    queryFn: () => fetchLandingCarouselSlides('ninxl'),
    staleTime: 60_000,
    placeholderData: () => getLandingCarouselFallbackSlides('ninxl'),
  })

  const slides = useMemo(
    () => demos ?? getLandingCarouselFallbackSlides('ninxl'),
    [demos],
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadedVideos, setLoadedVideos] = useState<Set<number>>(new Set())
  const [failedVideos, setFailedVideos] = useState<Set<number>>(new Set())
  const [isPlaying, setIsPlaying] = useState(true)
  const [isHovering, setIsHovering] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const openExpanded = useCallback(() => {
    // baby: pause the inline preview while the popup player is open
    videoRef.current?.pause()
    setIsPlaying(false)
    setIsExpanded(true)
  }, [])

  const handleExpandedOpenChange = useCallback((open: boolean) => {
    setIsExpanded(open)
    if (!open && videoRef.current) {
      void videoRef.current.play().catch(() => undefined)
      setIsPlaying(true)
    }
  }, [])

  // baby: clamp index if CMS returns fewer slides than previous render
  useEffect(() => {
    if (currentIndex >= slides.length) {
      setCurrentIndex(0)
    }
  }, [currentIndex, slides.length])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1))
    setIsPlaying(true)
  }, [slides.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1))
    setIsPlaying(true)
  }, [slides.length])

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index)
    setIsPlaying(true)
  }, [])

  const handleVideoCanPlay = useCallback((index: number) => {
    setLoadedVideos((prev) => new Set(prev).add(index))
    setFailedVideos((prev) => {
      const next = new Set(prev)
      next.delete(index)
      return next
    })
  }, [])

  const handleVideoError = useCallback((index: number) => {
    setFailedVideos((prev) => new Set(prev).add(index))
  }, [])

  const togglePlayPause = useCallback(() => {
    if (!videoRef.current) return
    if (isPlaying) {
      videoRef.current.pause()
    } else {
      void videoRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }, [isPlaying])

  const seekBackward = useCallback(() => {
    if (!videoRef.current) return
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5)
  }, [])

  const seekForward = useCallback(() => {
    if (!videoRef.current) return
    videoRef.current.currentTime = Math.min(
      videoRef.current.duration ?? 0,
      videoRef.current.currentTime + 5,
    )
  }, [])

  const safeIndex = Math.min(currentIndex, Math.max(slides.length - 1, 0))
  const isCurrentVideoLoaded = loadedVideos.has(safeIndex)
  const isCurrentVideoFailed = failedVideos.has(safeIndex)
  const currentFeature = slides[safeIndex]

  useEffect(() => {
    const v = videoRef.current
    if (v) {
      v.pause()
      if (safeIndex !== 0) v.currentTime = 0
    }
  }, [safeIndex])

  if (!currentFeature) return null

  return (
    <div className="grid gap-8 md:gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
      <div className="flex flex-col gap-6 order-2 lg:order-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentFeature.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-6"
          >
            <div className="flex items-center gap-3">
              <motion.div
                className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-100 shadow-sm"
                style={{ backgroundColor: `${currentFeature.color}15` }}
                initial={{ scale: 0.85, rotate: -8 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <SlideIcon slide={currentFeature} className="h-6 w-6" />
              </motion.div>
              <span className="text-xs font-medium tracking-widest text-slate-500 bg-white px-2.5 py-1 rounded-full border border-slate-200">
                {currentFeature.step}
              </span>
            </div>
            <h3 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl leading-tight">
              {currentFeature.title}
            </h3>
            <p className="text-base text-slate-600 md:text-lg leading-relaxed">
              {currentFeature.description}
            </p>
          </motion.div>
        </AnimatePresence>
        <div className="flex items-center gap-2 mt-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => goToSlide(index)}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                safeIndex === index ? 'bg-[#21728d] w-6' : 'bg-slate-200 hover:bg-slate-300 w-2',
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3 mt-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPrevious}
            className="h-12 w-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-[#21728d] bg-[#88a1ac]/10"
          >
            <ChevronLeft className="h-5 w-5 text-[#21728d]" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={goToNext}
            className="h-12 w-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-[#21728d] bg-[#88a1ac]/10"
          >
            <ChevronRight className="h-5 w-5 text-[#21728d]" />
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-center order-1 lg:order-2">
        <motion.div
          className="relative w-full max-w-[500px]"
          key={`video-wrap-${currentFeature.id}`}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative rounded-xl border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-200/50">
            <div
              className="relative rounded-lg border border-slate-200 bg-slate-50 overflow-hidden aspect-video"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {!isCurrentVideoFailed ? (
                <video
                  ref={videoRef}
                  key={currentFeature.demoSrc}
                  className="absolute inset-0 w-full h-full object-cover z-10"
                  src={currentFeature.demoSrc}
                  poster={currentFeature.posterSrc}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="none"
                  onCanPlay={() => handleVideoCanPlay(safeIndex)}
                  onError={() => handleVideoError(safeIndex)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                />
              ) : null}
              {isCurrentVideoLoaded && !isCurrentVideoFailed ? (
                <div
                  className={cn(
                    'absolute inset-0 z-30 flex items-center justify-center bg-black/40 transition-opacity duration-300',
                    isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none',
                  )}
                >
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={seekBackward}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-110 border border-white/10"
                      aria-label="Rewind 5 seconds"
                    >
                      <RotateCcw className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={togglePlayPause}
                      className="flex h-14 w-14 items-center justify-center rounded-full bg-white/30 text-white backdrop-blur-sm transition-all hover:bg-white/40 hover:scale-110 border border-white/10"
                      aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                      {isPlaying ? <Pause className="h-7 w-7" /> : <Play className="h-7 w-7 ml-1" />}
                    </button>
                    <button
                      type="button"
                      onClick={seekForward}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-110 border border-white/10"
                      aria-label="Forward 5 seconds"
                    >
                      <RotateCw className="h-5 w-5" />
                    </button>
                    <ExpandDemoVideoButton onClick={openExpanded} />
                  </div>
                </div>
              ) : null}
              {!isCurrentVideoLoaded || isCurrentVideoFailed ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 z-0">
                  <div
                    className="flex h-20 w-20 items-center justify-center rounded-2xl mb-4 border border-slate-200"
                    style={{ backgroundColor: `${currentFeature.color}15` }}
                  >
                    <SlideIcon slide={currentFeature} className="h-10 w-10" />
                  </div>
                  <span className="text-sm font-medium text-slate-500">
                    {isCurrentVideoFailed ? 'Video unavailable' : 'Loading...'}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          <motion.div
            className="absolute -inset-10 -z-10 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: currentFeature.color }}
            animate={{ opacity: [0.15, 0.28, 0.15] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      </div>
      <LandingDemoVideoLightbox
        open={isExpanded}
        onOpenChange={handleExpandedOpenChange}
        title={currentFeature.title}
        demoSrc={currentFeature.demoSrc}
        posterSrc={currentFeature.posterSrc}
        aspect="video"
      />
    </div>
  )
})
